import { MAX_SKILLS_PER_CHAT } from '@server/types';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { Badge } from '@/_shared/components/ui/badge';
import { useOrpc } from '@/_shared/hooks/useOrpc';

import { MentionMenu, type MentionMenuItem } from './MentionMenu';

interface SkillMenuProps {
  /** Text typed after the leading "/" — used for substring filtering. */
  query: string;
  /**
   * Skill IDs already occupying a chat slot (session-loaded + pending in the composer).
   * Excluded from the menu; when size ≥ MAX_SKILLS_PER_CHAT the menu shows the cap message.
   */
  alreadyLoadedIds: Set<string>;
  onSelect: (skill: { id: string; title: string }) => void;
  onClose: () => void;
  /** DOM id for the listbox, so the composer can name it in `aria-controls`. */
  id: string;
  /** Reports the active option's DOM id, which the composer carries as `aria-activedescendant`. */
  onActiveOptionChange?: (optionId: string | undefined) => void;
}

/**
 * Autocomplete menu shown when the user types "/" in the chat composer.
 * Lists invokable skills (code-defined built-ins and enabled workspace skills)
 * that are not already loaded into the current session.
 *
 * Fetches via `orpc.skills.listInvokable` — results come from the QueryClient
 * cache shared with ChatConversation / ChatWelcome, so no additional network
 * request is made when those views already warmed the query.
 */
export function SkillMenu({
  query,
  alreadyLoadedIds,
  onSelect,
  onClose,
  id,
  onActiveOptionChange,
}: SkillMenuProps): React.ReactElement {
  const orpc = useOrpc();
  const atCap = alreadyLoadedIds.size >= MAX_SKILLS_PER_CHAT;

  const { data, isPending } = useQuery(orpc.skills.listInvokable.queryOptions({ input: {} }));

  const skills = data?.skills;
  const items: MentionMenuItem[] = useMemo(() => {
    if (atCap) return [];
    const available = (skills ?? []).filter((skill) => skill.enabled && !alreadyLoadedIds.has(skill.id));
    const needle = query.toLowerCase();
    const matching = query
      ? available.filter((s) => `${s.name}\n${s.description}`.toLowerCase().includes(needle))
      : available;
    return matching.map((skill) => ({
      id: skill.id,
      label: skill.name,
      description: skill.description,
      badge:
        skill.source === 'built_in' ? (
          <Badge variant="outline" className="shrink-0 text-[0.625rem] text-muted-foreground">
            Built-in
          </Badge>
        ) : undefined,
    }));
  }, [skills, alreadyLoadedIds, atCap, query]);

  let notice: string | undefined;
  if (isPending) notice = 'Loading skills…';
  else if (atCap) notice = `A chat can load at most ${String(MAX_SKILLS_PER_CHAT)} skills`;
  else if (items.length === 0) notice = 'No matching skills';

  return (
    <MentionMenu
      items={items}
      onSelect={(item) => onSelect({ id: item.id, title: item.label })}
      onClose={onClose}
      label="Skills"
      notice={notice}
      id={id}
      onActiveOptionChange={onActiveOptionChange}
      testId="skill-menu"
      optionPrefix="skill-option"
    />
  );
}
